
import React, { useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import Accordion, { CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER } from "src/components/Accordion";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { FilterButton, FilterButtonSelected } from "src/pages/Actions/Panel/styles";
import formatDate from "src/utils/formatDate";
import { IIntbb001BudgetGrouper, IRefActionsModalBudget } from "src/pages/Actions/Panel/interface";
import ModalBudget from "src/pages/Actions/Panel/components/modalBudget";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { IListProgram } from "./interface";
import ListOfProjects from "./listOfProject";
import { ProjectSituationFilter } from "./Components/ListProjectsLevel/interface";

const ListProgram: React.FC<IListProgram> = ({
	program,
	filters,
	layoutList,
	onCallbackProgram,
	openModalAction,
	openModalAddSprint,
	openModalChangeSprint,
	openModalChangeProgram,
	openModalDeleteProgram,
	onCallbackChangeProgram,
	onCallbackDeleteProgram,
	onCallbackChangeProject,
	openModalAddProject,
	openModalChangeProject,
	openModalDeleteProject,
	openModalDetails,
	openModalReleaseActions,
	openModalReorderProjects,
	openModalGantt
}) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { superUser, administrator } = useAuth();

	const [loading, setLoading] = useState(true);
	const [errorLoading, setErrorLoading] = useState(false);
	const [filterList, setFilterList] = useState<ProjectSituationFilter | undefined>(undefined);
	const { setLoading: setLoadingBackdrop } = useLoaderBackdrop();
	const modalActionBudgetRef = useRef<IRefActionsModalBudget>(null);
	
	const handleClickGroupers = async (_e: any, expanded: boolean, programId: number) => {		
		if(!expanded)
			return;

		try{
			setLoading(true);
			setErrorLoading(false);

			const {data} = await RCCServicesApi.getRequest({
				method: "Project/List",
				urlParameters: {
					programId,
					...filters,
					map: filters.map || "",
					perspective: filters.perspective || "",
					objective: filters.objective || "",
					startDate: filters.startDate == null ? "" : formatDate(filters.startDate),
					endDate: filters.endDate == null ? "" : formatDate(filters.endDate)  
				}
			});

			if(onCallbackChangeProgram){
				onCallbackChangeProgram({
					...program,
					prtba002: data
				});
			}

			setLoading(false);
	
		}catch(err){
			setErrorLoading(true);
			standardErrorMessages(err, translate("Erro ao carregar os agrupadores"), addToast);
			setLoading(false);
		}
	};

	const filterActionsOnClickCountBar = (situation: ProjectSituationFilter) => {
		let situationToUpdate: ProjectSituationFilter | undefined = situation;
		
		if(filterList && filterList === situation)
			situationToUpdate = undefined;
		
		setFilterList(situationToUpdate);
	};

	const showBudget = async (group: number, title: string, programId: number, projectId?: number) => {
		try{
			setLoadingBackdrop(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/Budget",
				urlParameters: {
					group, // 15 - Program | 16- Project
					id: programId,
					id2: projectId,
					id3: "",
					id4: "",
					f: filters.f || "",
					responsible: filters.responsible || "",
					authority: filters.authority || "",
					sector: filters.sector || "",
					situation: filters.situation || "2,1,0",
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : ""
				}
			});

			const updateLabelMonth: IIntbb001BudgetGrouper[] = data.map((dt: IIntbb001BudgetGrouper) => ({
				...dt,
				name: translate(`monthWorkspace:${dt.name}`)
			}));
			setLoadingBackdrop(false);

			const titleModal = group === 15 ? `${translate("Programa")}: ${title}` : `${translate("Projeto")}: ${title}`;
			modalActionBudgetRef.current?.actionButtonOpenBudget(titleModal, updateLabelMonth);

		}catch(err){
			setLoadingBackdrop(false);
			standardErrorMessages(err, translate("Erro ao carregar orçamento"), addToast);
		}
	};

	return (
		<>
			<Accordion 
				onChange={(e, expanded) => {e.stopPropagation(); handleClickGroupers(e, expanded, program.pra001Id);}}
				header={
					<>
						<RCCGrid 
							container
							item
							justifyContent="space-between"
							alignItems="center"
							xs={12}
						>
							<RCCGrid
								item
								md={9}
								xs={12}
							>
								<span className="accordion-program-title">
									{program.pra001Titulo}
								</span>		
							</RCCGrid>
							<RCCGrid
								container 
								item
								justifyContent="flex-end"
								md={3}
								xs={12}
								className={CLASSNAME_ACCORDION_HEADER_SHOW_ONLY_ON_HOVER}
							>
								
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE} >
									<RCCTooltip
										text={translate("Adicionar Projeto")}
										color="purple"
									>
										<RCCButton
											icon={{name: "plus", size: 12}}
											outlined
											color="purple"
											size="xs"
											layout="isRounded"
											onClick={(e) => {e.stopPropagation(); if(openModalAddProject) openModalAddProject(program);}}
										/>
									</RCCTooltip> 
									{(superUser || administrator || program.permission >= 2) && (
										<RCCTooltip
											text={translate("Editar Programa")}
											color="grey"
										>
											<RCCButton
												icon={{name: "pencil", size: 12}}
												outlined
												color="grey"
												size="xs"
												layout="isRounded"
												onClick={(e) => {e.stopPropagation(); if(openModalChangeProgram) openModalChangeProgram(program.pra001Id);}}
											/>
										</RCCTooltip>
									)}	
								</CheckPermission>

								<RCCTooltip
									text={translate("Mostrar Orçamento")}
									color="grey"
								>
									<RCCButton
										icon={{name: "dollar", size: 14}}
										outlined
										color="grey"
										size="xs"
										layout="isRounded"
										onClick={(e) => { e.stopPropagation(); showBudget(15, program.pra001Titulo, program.pra001Id);}}
									/>
								</RCCTooltip>

								<RCCTooltip
									text={`${translate("Visualizar")} Gantt`}
									color="grey"
								>
									<RCCButton
										icon={{name: "table", size: 12}}
										outlined
										color="grey"
										size="xs"
										layout="isRounded"
										onClick={(e) => {e.stopPropagation(); if(!openModalGantt) return; openModalGantt(program.pra001Id, `${translate("Programa")}: ${program.pra001Titulo}`, "program");}}
									/>
								</RCCTooltip>
								
							 	{(superUser || administrator || program.permission >= 2) && (
									 <>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE} >
											<RCCTooltip
												text={translate("Reordenar Projetos")}
												color="grey"
											>
												<RCCButton
													icon={{name: "exchange", size: 11}}
													outlined
													color="grey"
													size="xs"
													layout="isRounded"
													styleInline="svg{ transform: rotate(90deg); }"
													onClick={(e) => {e.stopPropagation(); if(openModalReorderProjects) openModalReorderProjects(program);}}
												/>
											</RCCTooltip>
										</CheckPermission>
										<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE} >
											<RCCTooltip
												text={translate("Excluir Programa")}
												color="red"
											>
												<RCCButton
													icon={{name: "trash", size: 12}}
													outlined
													color="red"
													size="xs"
													layout="isRounded"
													onClick={(e) => {e.stopPropagation(); if(openModalDeleteProgram) openModalDeleteProgram(program, onCallbackDeleteProgram);}}
												/>
											</RCCTooltip>
										</CheckPermission>	
									 </>
								 )}		

							</RCCGrid>
						</RCCGrid>
						<RCCGrid 
							container 
							justifyContent="flex-end"
						>
							{program.notStarted > 0  && (
								<RCCButton 
									color="purple" 
									styleInline={filterList && filterList !== ProjectSituationFilter.NOT_STARTED ? FilterButtonSelected : FilterButton}
									onClick={(e) => { e.stopPropagation(); filterActionsOnClickCountBar( ProjectSituationFilter.NOT_STARTED); }}
								>{program.notStarted} {translate("não iniciado(s)")}</RCCButton>
							)}
							{program.inProgress > 0 && (
								<RCCButton 
									color="blue" 
									styleInline={filterList && filterList !== ProjectSituationFilter.IN_PROGRESS ? FilterButtonSelected : FilterButton}
									onClick={(e) => { e.stopPropagation(); filterActionsOnClickCountBar(ProjectSituationFilter.IN_PROGRESS); }}
								>{program.inProgress} {translate("em andamento")}</RCCButton>
							)}
							{program.nextToExpire > 0  && (
								<RCCButton 
									color="yellow" 
									styleInline={filterList && filterList !== ProjectSituationFilter.NEXT_TO_EXPIRE ? FilterButtonSelected : FilterButton}
									onClick={(e) => { e.stopPropagation(); filterActionsOnClickCountBar(ProjectSituationFilter.NEXT_TO_EXPIRE); }}
								>{program.nextToExpire} {translate("a vencer")}</RCCButton>
							)}
							{program.delayed > 0  && (
								<RCCButton 
									color="red" 
									styleInline={filterList && filterList !== ProjectSituationFilter.DELAYED ? FilterButtonSelected : FilterButton}
									onClick={(e) => { e.stopPropagation(); filterActionsOnClickCountBar( ProjectSituationFilter.DELAYED); }}
								>{program.delayed} {translate("atrasado(s)")}</RCCButton>
							)}
							{program.finished > 0 && (
								<RCCButton 
									color="green" 
									styleInline={filterList && filterList !== ProjectSituationFilter.FINISHED ? FilterButtonSelected : FilterButton}
									onClick={(e) => { e.stopPropagation(); filterActionsOnClickCountBar(ProjectSituationFilter.FINISHED); }}
								>
									{program.finished} {translate("concluído(s)")}
								</RCCButton>
							)}
							<Title color="purple" size="sm" styleInline="margin 0 4px;">{translate("Andamento")}: {program.percentage || 0}%</Title>
							<Title color="purple" size="sm" styleInline="margin 0 4px;">{translate("Total")}: {program.count || 0}</Title>
						</RCCGrid>
					</>}
			>
				<>
					{
						loading ? (
							<Loader />
						) : (
							<>
								{errorLoading ? (
									<RCCAlert 
										title={translate("Atenção")}
										description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
									/>
								): (
									<>
										{program.prtba002 === undefined || program.prtba002.length === 0 ? (
											<RCCGrid 
												container 
												styleInline="margin: 10px"
											>
												<RCCAlert 
													title={translate("Atenção")}
													description={translate("Nenhum projeto encontrado")}
												/>
											</RCCGrid>
										): (
											<RCCGrid container item xs={12}>										
												<ListOfProjects
													layoutList={layoutList}
													projects={program.prtba002}
													onCallbackProgram={onCallbackProgram}
													onCallbackChangeProject={onCallbackChangeProject}
													openModalChangeProject={openModalChangeProject}
													openModalDeleteProject={openModalDeleteProject}
													openModalAction={openModalAction}
													openModalAddSprint={openModalAddSprint}
													openModalChangeSprint={openModalChangeSprint}
													openModalDetails={openModalDetails}
													openModalReleaseActions={openModalReleaseActions}
													openModalGantt={openModalGantt}
													showBudget={showBudget}
													filterList={filterList}
												/>					
											</RCCGrid>
										)}
									</>
								)}
							</>
						)
					}
				</>
			</Accordion>

			<ModalBudget 
				ref={modalActionBudgetRef} 
			/>
		</>
	);
};


export default ListProgram;
